import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Row, Col, Button, ButtonGroup, Modal as ConfirmationModal } from 'react-bootstrap'
import * as s from './EliminatorAdmin.scss'
import Logins from '../../Main/Admin/Logins'
import EliminatorConfigSummary from './EliminatorConfigSummary'
import EliminatorSetActiveSeason from './EliminatorSetActiveSeason'
import EliminatorEditConfiguration from './EliminatorEditConfiguration'
import EliminatorBroadcastMessage from './EliminatorBroadcastMessage'
import Modal from '@severed-links/common.modal'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import { resetEditConfig, saveConfig, getConfig } from '@severed-links/common.redherrings-reducers/eliminatorAdmin'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import OnlineUsers from '../../../components/OnlineUsers'
import SendPushNotification from '../../Notifications/SendPushNotification'

const EliminatorAdminDashboard = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const seasonId = useSelector(state => state.eliminator.seasonId)
    const seasonName = useSelector(state => state.eliminator.seasonName)
    const seasonMode = useSelector(state => state.eliminator.seasonMode)
    const isPlayoffWeek = seasonMode === 'Postseason'
    const week = useSelector(state => state.eliminator.week)
    const playoffWeek = useSelector(state => state.eliminator.playoffWeek)
    const loginInfo = useSelector(state => state.eliminatorAdmin.loginInfo)
    const editConfig = useSelector(state => state.eliminatorAdmin.editConfig)
    const onlineUsers = useSelector(state => state.eliminatorAdmin.onlineUsers)
    const [showActiveSeasonModal, setShowActiveSeasonModal] = useState(false)
    const [showEditConfig, setShowEditConfig] = useState(false)
    const [showBroadcastMessage, setShowBroadcastMessage] = useState(false)

    const [confirmation, setConfirmation] = useState(null)
    const [saving, setSaving] = useState(false)
    const saveConfiguration = async (approved = null) => {
        if (saving) return
        const request = approved ? { ...approved.request, confirmEarlyPickDeletion: approved.token } : { ...editConfig }
        setConfirmation(null)
        setSaving(true)
        try {
            const action = await dispatch(saveConfig(request))
            const payload = action.payload || {}
            if (payload.confirmationRequired && payload.deletedPickCount > 0) {
                setConfirmation({ request, token: payload.confirmationToken, message: payload.message })
            } else {
                dispatch(createNotification(payload))
                if (!action.error && payload.messageType === 'success') {
                    dispatch(getConfig(payload.seasonId))
                    dispatch(resetEditConfig())
                    setShowEditConfig(false)
                }
            }
        } catch (error) {
            dispatch(createNotification({ messageType: 'error', message: 'Unable to save configuration. Please try again.' }))
        }
        setSaving(false)
    }

    const buttons = [
        { _id: 1, icon: 'leaf', title: 'set active season', onClick: () => setShowActiveSeasonModal(true) },
        { _id: 2, icon: 'cog', title: 'edit configuration', onClick: () => setShowEditConfig(true) },
        { _id: 3, icon: 'envelope', title: 'send email', onClick: () => setShowBroadcastMessage(true) },
        { _id: 4, icon: 'user-plus', title: 'register players', onClick: () => navigate(`/eliminator/admin/register-players/${seasonName}`) },
        { _id: 5, icon: 'list-alt', title: 'reminder log', onClick: () => navigate(`/eliminator/admin/reminder-log/season/${seasonName}/${isPlayoffWeek ? 'postseason' : 'regular'}/week/${isPlayoffWeek ? playoffWeek : week}`) },
        { _id: 6, icon: 'money-bill-alt', title: 'view payment log', onClick: () => navigate(`/eliminator/admin/payments/${seasonName}`) },
        { _id: 7, icon: 'check', title: 'manual pick entry', onClick: () => navigate(`/eliminator/admin/manual-entry/${seasonName}/${seasonMode}/${(seasonMode === 'Regular' ? `week/${week}` : `playoff-week/${playoffWeek}`)}`) },
    ]
    return (
        <div className={s.container}>
            <ConfirmationModal show={!!confirmation} onHide={() => setConfirmation(null)} centered>
                <ConfirmationModal.Header closeButton><ConfirmationModal.Title>Delete earlier picks?</ConfirmationModal.Title></ConfirmationModal.Header>
                <ConfirmationModal.Body>{confirmation?.message}</ConfirmationModal.Body>
                <ConfirmationModal.Footer>
                    <Button variant='light' onClick={() => setConfirmation(null)}>Cancel</Button>
                    <Button variant='danger' disabled={saving} onClick={() => saveConfiguration(confirmation)}>Delete picks and save</Button>
                </ConfirmationModal.Footer>
            </ConfirmationModal>
            <div className={s.pageHeader}>
                <div className={s.eyebrow}>Eliminator Admin</div>
                <h2>Dashboard</h2>
            </div>
            <Row className={s.dashboardGrid}>
                <Col sm={6} xs={12}>
                    <section className={s.adminCard}>
                        <EliminatorConfigSummary />
                        <ButtonGroup className={s.dashboardButtonContainer} variant='light'>
                        {buttons && buttons.map(i =>
                            <Button key={`config-action-${i._id}`}
                                onClick={() => i.onClick()}
                                variant='light' className={s.button}>
                                <FontAwesomeIcon name={i.icon} />
                                {' '}
                                {i.title}
                            </Button>
                        )}
                        </ButtonGroup>
                    </section>
                </Col>
                <Col sm={6} xs={12}>
                    <section className={s.adminCard + ' ' + s.onlineCard}>
                        <div className={s.cardHeader}>
                            <h4>Online Users</h4>
                            <SendPushNotification />
                        </div>
                        <OnlineUsers users={onlineUsers} />
                    </section>
                    <section className={s.adminCard + ' ' + s.loginsCard}>
                        <Logins isDashboard />
                    </section>
                </Col>
            </Row>

            <Modal heading='Set active season' size={'sm'}
                show={showActiveSeasonModal}
                onClose={() => setShowActiveSeasonModal(false)}
                actionButtonText={null}>
                <EliminatorSetActiveSeason />
            </Modal>

            <Modal heading={'Edit ' + seasonName + ' Season configuration'} size={null}
                className={s.editConfigModal}
                dialogClassName={s.editConfigDialog}
                bodyClass={s.editConfigBody}
                actionButtonClassName={s.editConfigActionButton}
                show={showEditConfig}
                enforceFocus={!confirmation}
                actionButtonDisabled={saving}
                onClose={() => { setShowEditConfig(false); dispatch(resetEditConfig())}}
                actionButtonOnClick={() => saveConfiguration()}
                actionButtonText={'save configuration'}>
                <EliminatorEditConfiguration />
            </Modal>

            <Modal heading='Broadcast Message'
                className={s.broadcastModal}
                dialogClassName={s.broadcastDialog}
                bodyClass={s.broadcastBody}
                show={showBroadcastMessage}
                onClose={() => setShowBroadcastMessage(false)}
                showFooter={false}
                actionButtonText={null}>
                <EliminatorBroadcastMessage />
            </Modal>

        </div>
    )
}

export default EliminatorAdminDashboard
